/**
 * useWorks — 作品 CRUD 封装(M2)
 *
 *   • useMyWorks       我的作品列表(含草稿)
 *   • usePublicWorks   公开作品列表(首页 / 广场)
 *   • useWorkDetail    单作品详情 + 分集
 */
import { useCallback, useRef, useState } from 'react'

import {
  ApiError,
  worksApi,
  type SilkEpisode,
  type SilkWork,
  type SilkWorkStatus,
} from '@/api'
import { useHasLoadedRef, useRefetchWhenLocaleChanges } from '@/shared/hooks/useLocaleRefetch'

/* ----------------------- 我的作品 ----------------------- */

interface MyWorksState {
  items: SilkWork[]
  total: number
  loading: boolean
  error: string | null
}

interface UseMyWorksOptions {
  enabled?: boolean
  status?: SilkWorkStatus
  categoryCode?: string
  limit?: number
  offset?: number
}

const MY_INIT: MyWorksState = { items: [], total: 0, loading: false, error: null }

export function useMyWorks(opts: UseMyWorksOptions = {}) {
  const hasLoadedRef = useHasLoadedRef()
  const { enabled = true, status, categoryCode, limit = 20, offset = 0 } = opts
  const [state, setState] = useState<MyWorksState>(MY_INIT)

  const refresh = useCallback(
    async (silent = false) => {
      if (!enabled) return
      if (!silent) {
        setState((s) => ({ ...s, loading: true, error: null }))
      }
      try {
        const list = await worksApi.myList({
          status,
          category_code: categoryCode || undefined,
          limit,
          offset,
        })
        setState({
          items: list.items,
          total: list.total,
          loading: false,
          error: null,
        })
        hasLoadedRef.current = true
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : (err as Error).message
        setState((s) => ({ ...s, loading: false, error: msg }))
      }
    },
    [enabled, status, categoryCode, limit, offset, hasLoadedRef],
  )

  useRefetchWhenLocaleChanges(refresh, hasLoadedRef)

  return { ...state, refresh: () => refresh(false) }
}

/* ----------------------- 公开作品 ----------------------- */

interface PublicWorksState {
  items: SilkWork[]
  total: number
  loading: boolean
  error: string | null
}

interface UsePublicWorksOptions {
  category?: string
  categoryCode?: string
  orderBy?: 'published_at' | 'view_count' | 'like_count' | 'created_at' | 'curated'
  limit?: number
  offset?: number
  skip?: boolean
}

const PUB_INIT: PublicWorksState = { items: [], total: 0, loading: false, error: null }

export function usePublicWorks(opts: UsePublicWorksOptions = {}) {
  const hasLoadedRef = useHasLoadedRef()
  const { category, categoryCode, orderBy = 'published_at', limit = 20, offset = 0, skip = false } = opts
  const [state, setState] = useState<PublicWorksState>(PUB_INIT)

  const refresh = useCallback(
    async (silent = false) => {
      if (skip) {
        setState({ ...PUB_INIT })
        return
      }
      if (!silent) {
        setState((s) => ({ ...s, loading: true, error: null }))
      }
      try {
        const list = await worksApi.publicList({
          category,
          category_code: categoryCode || undefined,
          order_by: orderBy,
          limit,
          offset,
        })
        setState({
          items: list.items,
          total: list.total,
          loading: false,
          error: null,
        })
        hasLoadedRef.current = true
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : (err as Error).message
        setState((s) => ({ ...s, loading: false, error: msg }))
      }
    },
    [category, categoryCode, orderBy, limit, offset, skip, hasLoadedRef],
  )

  useRefetchWhenLocaleChanges(refresh, hasLoadedRef)

  return { ...state, refresh: () => refresh(false) }
}

/* ----------------------- 作品详情 ----------------------- */

interface WorkDetailState {
  work: SilkWork | null
  episodes: SilkEpisode[]
  loading: boolean
  error: string | null
  notFound: boolean
}

const DETAIL_INIT: WorkDetailState = {
  work: null,
  episodes: [],
  loading: false,
  error: null,
  notFound: false,
}

export function useWorkDetail(idOrPublicId: string | number | null) {
  const hasLoadedRef = useHasLoadedRef()
  const [state, setState] = useState<WorkDetailState>(DETAIL_INIT)
  const keepContentRef = useRef(false)
  keepContentRef.current = !!state.work

  const refresh = useCallback(
    async (silent = false) => {
      if (idOrPublicId == null || idOrPublicId === '') {
        setState({ ...DETAIL_INIT })
        return
      }
      // 切换作品时保留上一帧画面，避免整页黑屏闪烁
      const soft = silent || keepContentRef.current
      if (!soft) {
        setState((s) => ({ ...s, loading: true, error: null, notFound: false }))
      } else {
        setState((s) => ({ ...s, error: null, notFound: false }))
      }
      try {
        const [work, eps] = await Promise.all([
          worksApi.get(idOrPublicId),
          worksApi.episodes(idOrPublicId, { limit: 100, offset: 0 }),
        ])
        setState({
          work,
          episodes: eps.items,
          loading: false,
          error: null,
          notFound: false,
        })
        hasLoadedRef.current = true
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setState({ ...DETAIL_INIT, notFound: true })
          return
        }
        const msg = err instanceof ApiError ? err.message : (err as Error).message
        setState((s) => ({ ...s, loading: false, error: msg }))
      }
    },
    [idOrPublicId, hasLoadedRef],
  )

  useRefetchWhenLocaleChanges(refresh, hasLoadedRef)

  return { ...state, refresh: () => refresh(false) }
}
